import React, { useState } from 'react';

import { useBlankCover } from '../../contexts/blank-cover';

import nocover from '../../../img/nocover.png';
import cypressCover from '../../../img/cypress-cover.jpg';
import { PRODUCT_CLASS_CAMPAIGN } from '../../store/constants';
import { ProductOut } from '../../store/api';

type Props = {
    readonly product: Pick<
        ProductOut,
        'id' | 'productClass' | 'currentIssueId' | 'campaignDetails' | 'title' | 'coverUrl'
    >;
    readonly onMouseEnter?: () => void;
    readonly onMouseLeave?: () => void;
    readonly minSizeInPx?: number;
};

// TODO the parsing logic should be removed when all coverUrls come without any query parameter
const coverUrlRegex = /\?.*$/;

/**
 * This function returns the given cover URL without all query parameters. We need this function, since the
 * cover URLs sent by the backend always contain the suffix "?w=157", but we want to provide this parameter
 * by ourselves.
 *
 * @param coverUrl
 */

function parseCoverUrl(coverUrl: string | undefined) {
    if (!coverUrl) {
        return undefined;
    }

    const match = coverUrlRegex.exec(coverUrl);
    if (!match) {
        return coverUrl;
    }

    return coverUrl.substr(0, coverUrl.length - match[0].length);
}

const ProductCover = ({
    product: { id: productId, productClass, currentIssueId, campaignDetails, coverUrl, title },
    onMouseEnter,
    onMouseLeave,
    minSizeInPx = 300
}: Props) => {
    const coverProductId =
        (productClass === PRODUCT_CLASS_CAMPAIGN ? campaignDetails?.standardProductId : currentIssueId) ?? productId;

    const parsedCoverUrl = parseCoverUrl(coverUrl);
    const providedCoverServiceCoverUrl = `${parsedCoverUrl}?w=${minSizeInPx}`;

    const assembledCoverServiceCoverUrl = `https://covers.borrowbox.com/v3/covers/${coverProductId}?w=${minSizeInPx}`;

    const [error, setError] = useState<string | null>(null);
    const isBlankCover = useBlankCover();

    // HACK: Cypress test runner created without reason tens of thousands of requests for i.e. bundles overview page aka
    //       "Curated Collections", when trying to load covers. Returning a static image from this component resolved
    //       this issue.
    //       Additionally skipping the loading of correct images via cover service speeds up the tests.
    if (window.Cypress) {
        return (
            <img
                src={cypressCover}
                alt="placeholder cover for cypress test runner"
                title={`${title} - Cypress Placeholder Cover`}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
            />
        );
    }

    if (
        (!parsedCoverUrl || error === 'COVER_SERVICE_ERROR') &&
        providedCoverServiceCoverUrl !== assembledCoverServiceCoverUrl
    ) {
        // Fallback if cover is not available via cover service but product has a cover url
        return (
            <img
                src={assembledCoverServiceCoverUrl}
                data-testid="product-cover"
                alt={title}
                title={title}
                onError={() => setError('COVER_URL_ERROR')}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
            />
        );
    }

    if (isBlankCover || error) {
        return (
            <img
                src={nocover}
                data-testid="product-cover"
                alt="cover currently not available"
                title={`${title} - Cover currently not available`}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
            />
        );
    }

    return (
        <img
            src={providedCoverServiceCoverUrl}
            data-testid="product-cover"
            alt={title}
            title={title}
            onError={() => setError('COVER_SERVICE_ERROR')}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        />
    );
};

ProductCover.displayName = 'ProductCover';

export default ProductCover;
